import { httpClient } from '../api/http-client';

export interface NovedadNomina {
  id: string;
  nominaId: string;
  tipo: 'FALTA' | 'HORA_EXTRA' | 'BONIFICACION' | 'DESCUENTO_OTRO' | 'FESTIVO';
  descripcion?: string;
  cantidad: number;
  valorUnitario: number;
  valorTotal: number;
  fecha: string;
}

export interface Nomina {
  id: string;
  usuarioId: string;
  periodoInicio: string;
  periodoFin: string;
  salarioMensual: number;
  diasLaborados: number;
  domingosTrabajados: number;
  totalDevengado: number;
  totalDescuentos: number;
  totalBonificaciones: number;
  totalPagar: number;
  estado: 'PENDIENTE' | 'PAGADA';
  observaciones?: string;
  novedades: NovedadNomina[];
  usuario?: { id: string; nombreCompleto: string; email: string };
  creadoEn: string;
  actualizadoEn: string;
}

export interface CreateNominaData {
  usuarioId: string;
  periodoInicio: string;
  periodoFin: string;
  salarioMensual?: number;
  diasLaborados?: number;
  domingosTrabajados?: number;
  novedades?: Array<{
    tipo: 'FALTA' | 'HORA_EXTRA' | 'BONIFICACION' | 'DESCUENTO_OTRO' | 'FESTIVO';
    descripcion?: string;
    cantidad: number;
    valorUnitario: number;
    fecha: string;
  }>;
  observaciones?: string;
}

export interface PaginatedNominas {
  success: boolean;
  data: Nomina[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface PagoExtra {
  id: string;
  usuarioId: string;
  tipo: 'FESTIVO' | 'HORA_EXTRA' | 'RECARGO_NOCTURNO' | 'DOMINGO' | 'OTRO';
  descripcion?: string;
  monto: number;
  fecha: string;
  estado: 'PENDIENTE' | 'PAGADO';
  creadoEn: string;
  actualizadoEn: string;
  usuario?: { id: string; nombreCompleto: string; email: string };
}

export interface PaginatedPagosExtras {
  success: boolean;
  data: PagoExtra[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const NominaService = {
  async list(page = 1, limit = 10, filters?: { usuarioId?: string; estado?: string; search?: string; desde?: string; hasta?: string }) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (filters?.usuarioId) params.append('usuarioId', filters.usuarioId);
    if (filters?.estado) params.append('estado', filters.estado);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.desde) params.append('desde', filters.desde);
    if (filters?.hasta) params.append('hasta', filters.hasta);
    const response = await httpClient.get<PaginatedNominas>(`/nominas?${params.toString()}`);
    return response.data;
  },

  async getById(id: string) {
    const response = await httpClient.get<{ success: boolean; data: Nomina }>(`/nominas/${id}`);
    return response.data;
  },

  async create(data: CreateNominaData) {
    const response = await httpClient.post<{ success: boolean; data: Nomina }>('/nominas', data);
    return response.data;
  },

  async update(id: string, data: { estado?: 'PENDIENTE' | 'PAGADA'; observaciones?: string }) {
    const response = await httpClient.patch<{ success: boolean; data: Nomina }>(`/nominas/${id}`, data);
    return response.data;
  },

  async pagar(id: string) {
    const response = await httpClient.patch<{ success: boolean; data: Nomina }>(`/nominas/${id}/pagar`);
    return response.data;
  },

  async delete(id: string) {
    const response = await httpClient.delete<{ success: boolean; message: string }>(`/nominas/${id}`);
    return response.data;
  },

  // --- Pagos Extras ---

  async listPagosExtras(page = 1, limit = 10, filters?: { usuarioId?: string; estado?: string; search?: string }) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (filters?.usuarioId) params.append('usuarioId', filters.usuarioId);
    if (filters?.estado) params.append('estado', filters.estado);
    if (filters?.search) params.append('search', filters.search);
    const response = await httpClient.get<PaginatedPagosExtras>(`/nominas/pagos-extras?${params.toString()}`);
    return response.data;
  },

  async createPagoExtra(data: { usuarioId: string; tipo: PagoExtra['tipo']; descripcion?: string; monto: number; fecha: string }) {
    const response = await httpClient.post<{ success: boolean; data: PagoExtra }>('/nominas/pagos-extras', data);
    return response.data;
  },

  async pagarPagoExtra(id: string) {
    const response = await httpClient.patch<{ success: boolean; data: PagoExtra }>(`/nominas/pagos-extras/${id}/pagar`);
    return response.data;
  },

  async deletePagoExtra(id: string) {
    const response = await httpClient.delete<{ success: boolean; message: string }>(`/nominas/pagos-extras/${id}`);
    return response.data;
  },
};
